import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  TimeAttendanceMarkStatus,
  TimeAttendanceMarkType,
} from '@/generated/graphql/types';

import {
  type TDailyMarkingContext,
  type TDaySchedule,
  type TExpectedMark,
  type TShiftContext,
  useGetDailyMarking,
} from '../hooks/use-get-daily-marking';

type TWidgetContext = {
  loading: boolean;
  error: boolean;
  refetch: () => void;
  data?: TDailyMarkingContext;
  timeZone: string;
  timer: TTimerResult;
};

const WidgetClockContext = createContext<TWidgetContext | undefined>(undefined);

type TWidgetProviderProps = {
  children: React.ReactNode;
  /**
   * Callback to be called when a mark is registered.
   * Useful to trigger a refetch or process after a successful mark.
   */
  onMarkRegistered?: () => void;
  timeZone: string;
};
export const WidgetClockProvider = ({
  children,
  timeZone,
}: TWidgetProviderProps) => {
  const { data, loading, error, refetch } = useGetDailyMarking({ timeZone });

  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const [, setTick] = useState(0);

  // When all marks are completed (exit marked), reset the context so the UI
  // shows a clean "ready for new entry" state: fresh pending marks, zeroed timer,
  // and next_pending_mark back to ENTRY. After the user marks a new entry,
  // refetch() returns fresh backend data and this override stops applying.
  const resolvedData = useMemo<TDailyMarkingContext | undefined>(() => {
    if (!data?.all_marks_completed) {
      return data;
    }

    return {
      ...data,
      all_marks_completed: false,
      next_pending_mark: TimeAttendanceMarkType.Entry,
      expected_marks: [
        TimeAttendanceMarkType.Entry,
        TimeAttendanceMarkType.BreakStart,
        TimeAttendanceMarkType.BreakEnd,
        TimeAttendanceMarkType.Exit,
      ].map(mark_type => ({
        __typename: 'TimeExpectedMark' as const,
        mark_type,
        status: TimeAttendanceMarkStatus.OutOfSchedule,
        marked_at: undefined,
      })),
    };
  }, [data]);

  const timerState = useMemo(
    () => getTimerState(resolvedData?.expected_marks ?? []),
    [resolvedData],
  );

  const shouldTick = timerState === 'running';

  // Start/stop the 1s interval based on timer state.
  // Only ticks while 'running' — paused/stopped skips the interval entirely.
  useEffect(() => {
    if (!shouldTick) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }

      return;
    }

    intervalRef.current = setInterval(() => {
      setTick(prev => prev + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [shouldTick]);

  const timer = calculateTimer(
    resolvedData?.expected_marks ?? [],
    resolvedData?.shift_context,
    resolvedData?.next_schedule,
    Date.now(),
  );

  return (
    <WidgetClockContext.Provider
      value={{ loading, error, refetch, timeZone, timer, data: resolvedData }}
    >
      {children}
    </WidgetClockContext.Provider>
  );
};

export const useWidgetClockContext = () => {
  const context = useContext(WidgetClockContext);
  if (!context) {
    throw new Error('useWidgetContext must be used within a WidgetProvider');
  }
  return context;
};

const getMarkTime = (
  marks: TExpectedMark[],
  markType: TimeAttendanceMarkType,
): number | undefined => {
  const mark = marks.find(m => m.mark_type === markType && m.marked_at);

  if (!mark?.marked_at) {
    return undefined;
  }

  return new Date(mark.marked_at).getTime();
};

// Timer state machine: idle → running → paused → running → stopped.
// Derived from which marks have been registered (have a non-null marked_at).
type TTimerState = 'idle' | 'running' | 'paused' | 'stopped';

const getTimerState = (marks: TExpectedMark[]): TTimerState => {
  const hasMarkedEntry =
    getMarkTime(marks, TimeAttendanceMarkType.Entry) !== undefined;
  const hasMarkedBreakStart =
    getMarkTime(marks, TimeAttendanceMarkType.BreakStart) !== undefined;
  const hasMarkedBreakEnd =
    getMarkTime(marks, TimeAttendanceMarkType.BreakEnd) !== undefined;
  const hasMarkedExit =
    getMarkTime(marks, TimeAttendanceMarkType.Exit) !== undefined;

  if (!hasMarkedEntry) {
    return 'idle';
  }

  if (hasMarkedExit) {
    return 'stopped';
  }

  if (hasMarkedBreakStart && !hasMarkedBreakEnd) {
    return 'paused';
  }

  return 'running';
};

// ─── Timer calculation ───────────────────────────────────────────────

/** A contiguous period of work, measured in epoch milliseconds.
 * Breaks split the day into multiple segments: [entry→breakStart, breakEnd→exit]. */
type TTimeSegment = { start: number; end: number };

// 24 hours in ms — fallback cap when no shift or next schedule exists,
// prevents the timer from running indefinitely if the employee forgets to clock out.
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Converts marks into time ranges when the employee was actively working.
 *
 * Example with lunch break:
 *   Entry 9:00, BreakStart 12:00, BreakEnd 13:00, now 16:00
 *   → [9:00→12:00] + [13:00→16:00]  (lunch excluded)
 *
 * Example without break:
 *   Entry 9:00, now 16:00
 *   → [9:00→16:00]  (single segment)
 *
 * Open-ended segments (no exit yet) use `now` as their end. */
const buildWorkSegments = (
  marks: TExpectedMark[],
  now: number,
): TTimeSegment[] => {
  const entryTime = getMarkTime(marks, TimeAttendanceMarkType.Entry);

  if (!entryTime) {
    return [];
  }

  const breakStartTime = getMarkTime(marks, TimeAttendanceMarkType.BreakStart);
  const breakEndTime = getMarkTime(marks, TimeAttendanceMarkType.BreakEnd);
  const exitTime = getMarkTime(marks, TimeAttendanceMarkType.Exit);

  // No break taken → single continuous segment from entry to exit (or now)
  if (!breakStartTime) {
    return [{ start: entryTime, end: exitTime ?? now }];
  }

  // Break started → morning segment ends at break start
  const morningSegment: TTimeSegment = {
    start: entryTime,
    end: breakStartTime,
  };

  // Break not ended yet → only the morning segment counts
  if (!breakEndTime) {
    return [morningSegment];
  }

  // Break ended → afternoon segment resumes from break end
  const afternoonSegment: TTimeSegment = {
    start: breakEndTime,
    end: exitTime ?? now,
  };

  return [morningSegment, afternoonSegment];
};

/** Trims a segment to fit inside a time window [windowStart, windowEnd].
 *
 * Example: segment [8:45→12:00] + window [9:00→18:00] → [9:00→12:00]
 *   The 8:45→9:00 part is outside the window, so it gets trimmed.
 *
 * Returns undefined if the segment falls entirely outside the window. */
const clampSegmentToWindow = (
  segment: TTimeSegment,
  windowStart: number,
  windowEnd: number,
): TTimeSegment | undefined => {
  const clampedStart = Math.max(segment.start, windowStart);
  const clampedEnd = Math.min(segment.end, windowEnd);

  // Segment is entirely outside the window
  if (clampedStart >= clampedEnd) {
    return undefined;
  }

  return { start: clampedStart, end: clampedEnd };
};

/** Sums the duration of all segments in seconds. */
const sumSegmentDurations = (segments: TTimeSegment[]): number =>
  segments.reduce((total, seg) => total + (seg.end - seg.start) / 1000, 0);

/** Determines when the timer should auto-stop to prevent running indefinitely.
 * Stops 1 minute before the next shift starts, or after 24 hours as fallback. */
const getAutoCloseTime = (
  entryTime: number,
  nextSchedule?: TDaySchedule | null,
): number => {
  if (nextSchedule?.entry_time) {
    // 1 minute before next shift to avoid overlap between two shift timers
    return new Date(nextSchedule.entry_time).getTime() - 60_000;
  }

  return entryTime + TWENTY_FOUR_HOURS_MS;
};

type TTimerResult = {
  /** In-schedule worked time (0 when no shift context). */
  workedSeconds: number;
  /** Time outside the shift window (all elapsed time when no shift context). */
  outOfScheduleSeconds: number;
  /** Shift's expected duration in seconds (0 when no shift context). */
  expectedSeconds: number;
  /**
   * The value the timer UI should display as the main counter.
   * - With shift: equals workedSeconds (in-schedule time).
   * - Without shift (rest day or unassigned): equals total elapsed time
   *   (workedSeconds + outOfScheduleSeconds), since there's no schedule
   *   to split against.
   */
  displaySeconds: number;
  /** Whether the employee has a shift assigned for the current day. */
  hasShift: boolean;
};

/** Splits total worked time into two buckets: in-schedule vs out-of-schedule.
 *
 * The shift's `effective_*` fields define the valid window — they are the scheduled
 * times expanded by tolerance, pre-calculated by the backend so we don't handle
 * tolerance logic here.
 *
 * Steps:
 * 1. Build work segments from marks (lunch break excluded)
 * 2. Cap segments at auto-close time (safety net)
 * 3. No shift? → all time is out-of-schedule
 * 4. Has shift? → clamp segments to the effective window
 *    - Time inside window  → workedSeconds (capped at expected_minutes)
 *    - Time outside window → outOfScheduleSeconds */
const calculateTimer = (
  marks: TExpectedMark[],
  shiftContext: TShiftContext | null | undefined,
  nextSchedule: TDaySchedule | null | undefined,
  now: number,
): TTimerResult => {
  const segments = buildWorkSegments(marks, now);
  const entryTime = getMarkTime(marks, TimeAttendanceMarkType.Entry);

  if (segments.length === 0 || !entryTime) {
    return {
      workedSeconds: 0,
      outOfScheduleSeconds: 0,
      expectedSeconds: 0,
      displaySeconds: 0,
      hasShift: !!shiftContext,
    };
  }

  // Don't count time beyond the auto-close threshold
  const autoCloseTime = getAutoCloseTime(entryTime, nextSchedule);
  const effectiveNow = Math.min(now, autoCloseTime);

  // Trim open-ended segments so they don't count beyond the current moment.
  // buildWorkSegments uses `now` as the end for segments without an exit mark,
  // but effectiveNow may be earlier than `now` if auto-close kicked in.
  //
  // Example A — all 4 marks registered, day is complete:
  //   Entry 9:00, BreakStart 12:00, BreakEnd 13:00, Exit 18:00
  //   Segments: [9:00→12:00] + [13:00→18:00]  (both closed)
  //   effectiveNow = 19:00 (current time, no effect)
  //   After cap:  [9:00→12:00] + [13:00→18:00]  ← no change, all ends < effectiveNow
  //
  // Example B — 3 marks, still working (no exit yet):
  //   Entry 9:00, BreakStart 12:00, BreakEnd 13:00, Exit ???
  //   Segments: [9:00→12:00] + [13:00→now(15:30)]
  //   effectiveNow = 15:30
  //   After cap:  [9:00→12:00] + [13:00→15:30]  ← open segment trimmed to now
  //
  // Example C — forgot to clock out, auto-close activates:
  //   Entry 9:00 yesterday, no break, no exit, now = 10:00 today
  //   Next shift starts 9:00 today → autoClose = 8:59 today
  //   Segments: [9:00 yesterday → now(10:00 today)]
  //   effectiveNow = min(10:00 today, 8:59 today) = 8:59 today
  //   After cap:  [9:00 yesterday → 8:59 today]  ← trimmed by 1h 1min
  const segmentsCappedToNow = segments.map(seg => ({
    start: seg.start,
    end: Math.min(seg.end, effectiveNow),
  }));

  // Total time the employee was actively working (breaks excluded, capped to now)
  const totalWorkedSeconds = sumSegmentDurations(segmentsCappedToNow);

  // ── No shift context (rest day or unassigned shift) ──
  // The backend returns `shift_context: null` for both rest days and employees
  // with no shift assigned — the two cases are indistinguishable from the API.
  // Without a shift there's no schedule window, so all elapsed time is
  // out-of-schedule and workedSeconds stays at 0. displaySeconds provides
  // the total elapsed time for the UI to show as the main counter.
  if (!shiftContext) {
    const oos = Math.floor(totalWorkedSeconds);

    return {
      workedSeconds: 0,
      outOfScheduleSeconds: oos,
      expectedSeconds: 0,
      displaySeconds: oos,
      hasShift: false,
    };
  }

  // ── Shift assigned: split time into in-window vs out-of-window ──

  // The effective window = scheduled shift ± tolerance.
  // e.g. shift 9:00–18:00 with 15min before tolerance → window 8:45–18:00
  const windowStart = shiftContext.effective_shift_start
    ? new Date(shiftContext.effective_shift_start).getTime()
    : entryTime;
  const windowEnd = shiftContext.effective_shift_end
    ? new Date(shiftContext.effective_shift_end).getTime()
    : entryTime + TWENTY_FOUR_HOURS_MS;

  const expectedSeconds = shiftContext.expected_minutes
    ? shiftContext.expected_minutes * 60
    : 0;

  // workedSeconds can't exceed the shift's expected duration
  const maxWorkedSeconds =
    expectedSeconds > 0 ? expectedSeconds : (windowEnd - windowStart) / 1000;

  // Keep only the portion of each segment that falls inside the shift window
  const inWindowSegments = segmentsCappedToNow
    .map(seg => clampSegmentToWindow(seg, windowStart, windowEnd))
    .filter((seg): seg is TTimeSegment => seg !== undefined);

  const inWindowSeconds = sumSegmentDurations(inWindowSegments);
  const workedSeconds = Math.min(Math.floor(inWindowSeconds), maxWorkedSeconds);

  // Everything not counted as "worked" is out-of-schedule:
  // early arrivals, late stays, and any time beyond the expected duration cap
  const outOfScheduleSeconds = Math.floor(
    Math.max(0, totalWorkedSeconds - workedSeconds),
  );

  return {
    workedSeconds,
    outOfScheduleSeconds,
    expectedSeconds,
    displaySeconds: workedSeconds,
    hasShift: true,
  };
};
